import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React from 'react';
import { ContentCopyExtension } from '../../ContentCopyExtension/ContentCopyExtension';
import Box from '@mui/system/Box';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import { Tooltip } from '../Tooltip/Tooltip';

export type SectionGridItemProps = {
  label: string;
  value?: string | null;
  testId?: string;
  children?: React.ReactElement;
  customGridSizes?: CustomGridSizes | null;
  forceDisplay?: boolean;
  isLoading?: boolean;
};

export type CustomGridSizes = {
  xsSize?: number | 'auto';
  smSize?: number | 'auto';
  mdSize?: number | 'auto';
  lgSize?: number | 'auto';
  xlSize?: number | 'auto';
};

const GridStyled = styled(Grid)({
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  overflow: 'hidden',
  minWidth: 0,
});

const SectionGridItemSuspense = ({
  loading,
  children,
  ['data-testid']: testId,
}: {
  loading: boolean;
  children: React.ReactNode;
  ['data-testid']?: string;
}) => {
  return loading ? <Skeleton data-testid={`${testId}-skeleton-loader`} /> : children;
};

export const SectionGridItem: React.FC<SectionGridItemProps> = ({
  label,
  value,
  testId,
  children,

  customGridSizes,
  forceDisplay = false,
  isLoading,
}) => {
  const gridSizes = {
    xs: customGridSizes?.xsSize || 12,
    sm: customGridSizes?.smSize || 6,
    md: customGridSizes?.mdSize || 4,
    lg: customGridSizes?.lgSize || 3,
    xl: customGridSizes?.xlSize || 2,
  };

  let valueToDisplay = value;
  if (forceDisplay && value === undefined) {
    valueToDisplay = null;
  }

  return valueToDisplay !== undefined || children ? (
    <Grid size={gridSizes}>
      <Grid container alignItems="center" spacing={1} columns={1}>
        <GridStyled
          size={6}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <Tooltip
            title={label}
            showArrow={true}
            variant="dark"
            placement={'bottom'}
            enterDelay={1000}
            enterTouchDelay={1000}
            enterNextDelay={1000}
          >
            <Typography variant="text2" color="text.secondary">
              {label}
            </Typography>
          </Tooltip>
        </GridStyled>
        <GridStyled size={6}>
          <SectionGridItemSuspense loading={!!isLoading} data-testid={testId}>
            {children ? (
              <Box data-testid={testId}>{children}</Box>
            ) : (
              <Typography variant="text1" color="text.primary" data-testid={testId}>
                <ContentCopyExtension value={value || '-'}>
                  <Tooltip
                    title={value || '-'}
                    showArrow={true}
                    variant="dark"
                    placement={'bottom'}
                    enterDelay={1000}
                    enterTouchDelay={1000}
                    enterNextDelay={1000}
                  >
                    <Box
                      data-testid={`${testId}.value`}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {value || '-'}
                    </Box>
                  </Tooltip>
                </ContentCopyExtension>
              </Typography>
            )}
          </SectionGridItemSuspense>
        </GridStyled>
      </Grid>
    </Grid>
  ) : null;
};
